// Pure room-level compare-and-merge. Other devices' untouched rooms/fields survive.
export const BOARD_LISTS = ['vacantRooms', 'occupiedRooms', 'checkoutRooms', 'serviceRooms'];
export const roomKey = (item) => String(typeof item === 'object' ? item?.room : item ?? '').trim().toUpperCase();
const canonical = (value) => {
  if (value === undefined) return null;
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    if (typeof value.toMillis === 'function') return value.toMillis();
    return Object.fromEntries(Object.keys(value).sort().map(k => [k, canonical(value[k])]));
  }
  return value;
};
const equal = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
const mapList = (list = []) => new Map(list.map(item => [roomKey(item), item]).filter(([key]) => key));
export class RoomConflictError extends Error {
  constructor(room) { super(`Room ${room} doosre device par update hua hai. Fresh board load ho gaya; action dobara karein.`); this.code = 'room-conflict'; }
}
export function planBoardMutation(before = {}, after = {}) {
  const changes = [];
  for (const list of BOARD_LISTS) {
    const old = mapList(before[list]); const next = mapList(after[list]);
    for (const room of new Set([...old.keys(), ...next.keys()])) {
      const previous = old.get(room); const desired = next.get(room);
      if (!equal(previous, desired)) changes.push({ list, room, previous, desired });
    }
  }
  return { changes, rush: before.rushTempNameEnabled !== after.rushTempNameEnabled ? {previous: before.rushTempNameEnabled === true, desired: after.rushTempNameEnabled === true} : null };
}
const identity = item => `${item?.stayId || item?.id || ''}|${item?.status || ''}|${item?.detailsPending === true}`;
export function mergeBoardMutation(current = {}, operation) {
  const maps = Object.fromEntries(BOARD_LISTS.map(list => [list, mapList(current[list])]));
  for (const change of operation.changes) {
    const {list, room, previous, desired} = change; const latest = maps[list].get(room);
    if (equal(latest, desired)) continue; // An already applied operation is idempotent.
    if (previous === undefined || desired === undefined || typeof previous !== 'object' || typeof desired !== 'object') {
      if (!equal(latest, previous)) throw new RoomConflictError(room);
      if (desired === undefined) maps[list].delete(room); else maps[list].set(room, desired);
      continue;
    }
    if (!latest || identity(latest) !== identity(previous)) throw new RoomConflictError(room);
    const merged = {...latest};
    for (const key of new Set([...Object.keys(previous), ...Object.keys(desired)])) {
      if (equal(previous[key], desired[key])) continue;
      if (!equal(latest[key], previous[key]) && !equal(latest[key], desired[key])) throw new RoomConflictError(room);
      if (desired[key] === undefined) delete merged[key]; else merged[key] = desired[key];
    }
    maps[list].set(room, merged);
  }
  const result = {...current};
  for (const list of BOARD_LISTS) result[list] = [...maps[list].values()];
  const physical = new Map();
  for (const list of BOARD_LISTS.slice(0,3)) {
    for (const room of maps[list].keys()) {
      if (physical.has(room)) throw new RoomConflictError(room);
      physical.set(room, list);
    }
  }
  if (operation.rush) {
    if ((current.rushTempNameEnabled === true) !== operation.rush.previous && (current.rushTempNameEnabled === true) !== operation.rush.desired) throw new RoomConflictError('settings');
    result.rushTempNameEnabled = operation.rush.desired;
  }
  return result;
}
export function assertRoomLockAllowsBoard(room, board, lock = {}) {
  const occupied = (board.occupiedRooms || []).find(item => roomKey(item) === room);
  const checkout = (board.checkoutRooms || []).find(item => roomKey(item) === room);
  if (lock.activeStayId && String(occupied?.stayId || occupied?.id || '') !== String(lock.activeStayId)) throw new RoomConflictError(room);
  if (occupied && !lock.activeStayId && ['cleaning', 'checkout_pending', 'maintenance_hold'].includes(lock.status)) throw new RoomConflictError(room);
  if (checkout && lock.lastStayId && checkout.stayId && String(checkout.stayId) !== String(lock.lastStayId)) throw new RoomConflictError(room);
}
// Board cards describe physical state; stay records own financial/guest fields.
export function hydrateOccupiedCards(cards, stays = new Map()) {
  return (cards || []).flatMap(card => {
    const stayId = card?.stayId || card?.id;
    const fresh = stays.get(stayId);
    if (!fresh) return [card];
    if (fresh.checkedOut || fresh.checkoutCompleted || fresh.status !== 'checked_in') return [];
    if (roomKey(fresh) !== roomKey(card)) {
      // A transaction's new board can arrive before its new stay snapshot.
      // Only preserve a proven newer shift card; ordinary stale cards still hide.
      const cardShift = Date.parse(card.lastRoomShiftAt || '');
      const stayShift = Date.parse(fresh.lastRoomShiftAt || '');
      if (card.previousRoom && roomKey({room: card.previousRoom}) === roomKey(fresh) &&
          Number.isFinite(cardShift) && (!Number.isFinite(stayShift) || cardShift > stayShift)) return [card];
      return [];
    }
    return [{...card,...fresh,room:card.room,stayId,category:card.category || fresh.category,roomCategory:card.roomCategory || fresh.roomCategory}];
  });
}

// Room inventory removal never touches stays, payments or guest history.
export function removeReadyRoom(board, lock, stays, {hotelId, room}) {
  const reject = () => {throw new Error(`Room ${room} remove nahi ho sakta: pehle guest/cleaning/room lock verify karein.`);};
  if (!hotelId || !room || (board.hotelId && board.hotelId !== hotelId)) reject();
  if (!(board.vacantRooms || []).some(item => roomKey(item) === room)) reject();
  if (BOARD_LISTS.slice(1).some(list => (board[list] || []).some(item => roomKey(item) === room))) reject();
  if (lock) reject();
  if ((stays || []).some(stay => stay.hotelId === hotelId && roomKey(stay) === room &&
      String(stay.status || '').trim().toLowerCase() === 'checked_in' &&
      stay.checkedOut !== true && stay.checkoutCompleted !== true)) reject();
  return {...board, vacantRooms: board.vacantRooms.filter(item => roomKey(item) !== room)};
}
